/* eslint-disable @typescript-eslint/no-shadow */
import { useMutation } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useOutletContext, useParams } from 'react-router-dom';

import {
  CalendarIcon,
  CheckBoxReferralIcon,
  TrashIcon,
  WarningIcon,
} from '../../../assets';
import { Confirm } from '../../../context/confirm/confirm';
import {
  UPDATE_PROPOSAL_USER,
  UPDATE_PROPOSAL_USER_STATUS,
} from '../../../graphql/proposal/mutation';
import { useNotification } from '../../../hooks/useNotification';
import type {
  TInfSettingsAction,
  TInfSettingsRecurrentAction,
} from '../../../types/action';
import type { ProposalInfluencerStatus } from '../../../types/proposal';
import type { SingleProposalOutletContext } from '../../../views/SingleProposal';
import { MonthActionRecurrent } from '../../campaigns/recurrents/MonthActionRecurrent';
import { CampaignPriceSections } from '../../campaigns/settings/CampaignPriceSection';
import ReferralCode from '../../campaigns/settings/influencer/ReferralCode';
import { Button } from '../../common/Button';
import { DatePickerInput } from '../../inputs/DatePicker';
import { TextInput } from '../../inputs/TextInput';
import { Toggle } from '../../inputs/Toggle';
import ProposalActionSettings from './ProposalActionSettings';
import ProposalActionSettingsRecurrent from './ProposalActionSettingsRecurrent';
// ─── Shared helpers ──────────────────────────────────────────────────────────

function newRowId(): string {
  return Math.floor(Math.random() * 1_000_000).toString();
}

const PACK_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Returns the next pack name in the "Opción X" series given the names already
 * in use. Falls back to the first unused letter, or appends a counter if all
 * 26 letters are exhausted.
 */
function nextPackName(existingNames: string[]): string {
  const usedLetters = new Set(
    existingNames
      .map((n) => {
        const m = /^Opción\s+([A-Z])$/i.exec(n.trim());
        return m ? m[1].toUpperCase() : null;
      })
      .filter((x): x is string => Boolean(x)),
  );
  const letters = PACK_LETTERS.split('');
  for (let i = 0; i < letters.length; i += 1) {
    const letter = letters[i];
    if (!usedLetters.has(letter)) {
      return `Opción ${letter}`;
    }
  }
  return `Opción ${existingNames.length + 1}`;
}

function formatCurrency(
  amount: number,
  currency: string | null | undefined,
): string {
  const safeCurrency = currency || '€';
  return `${amount.toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${safeCurrency}`;
}

function packSubtotal(
  actions: { total_amount?: number | string | null }[],
): number {
  return actions.reduce<number>((sum, a) => {
    const v = a.total_amount;
    // MoneyInput stores its value as a raw formatted string in RHF state
    // (see MoneyInput's onChange), not a number — coerce before summing so
    // edits to individual action prices are reflected in the pack subtotal.
    const parsed = typeof v === 'string' ? Number(v) : v;
    return (
      sum + (typeof parsed === 'number' && !Number.isNaN(parsed) ? parsed : 0)
    );
  }, 0);
}

type TPackAmountPillProps = {
  label: string;
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  currency: string;
};

/**
 * Numeric pill input used for the per-pack amounts (brand price and
 * influencer amount). Same locale rules as MoneyInput (`.` thousands, `,`
 * decimals, max 2 decimals, no negatives); the form field always receives a
 * plain number (or null when emptied).
 */
function PackAmountPill({
  label,
  value,
  onChange,
  currency,
}: TPackAmountPillProps) {
  return (
    <label
      className="flex flex-row items-center gap-2 whitespace-nowrap bg-influentia-light-blue text-dark-blue px-3 py-1.5 rounded-full text-xs font-bold"
      title={label}
    >
      <span>{label} ·</span>
      <NumericFormat
        value={value === undefined || value === null ? '' : value}
        valueIsNumericString
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={2}
        allowNegative={false}
        inputMode="decimal"
        onValueChange={(values, sourceInfo) => {
          // Ignore prop-driven updates (e.g. subtotal recalculation) so only
          // real user edits write back to the form.
          if (sourceInfo.source !== 'event') return;
          onChange(values.floatValue ?? null);
        }}
        aria-label={label}
        autoComplete="off"
        className="bg-white text-dark-blue rounded-[4px] px-2 py-0.5 text-xs font-bold w-[110px] focus:outline-none focus:ring-1 focus:ring-influentia-yellow"
      />
      <span>{currency}</span>
    </label>
  );
}

/**
 * Convert raw `activeInfluencer.packs` (or legacy `actions`) into a fully
 * hydrated pack list suitable for the form. Guarantees at least one pack.
 */
function hydrateOneTimePacks(activeInfluencer: any): TOneTimePackForm[] {
  const existing: any[] = activeInfluencer?.packs || [];
  if (existing.length > 0) {
    return existing.map((pack: any, idx: number) => ({
      id: pack.id,
      name: pack.name,
      position: typeof pack.position === 'number' ? pack.position : idx,
      total_amount: pack.total_amount ?? packSubtotal(pack.actions || []),
      influencer_amount: pack.influencer_amount ?? null,
      actions: (pack.actions || []).map((a: any) => ({
        id: a.id,
        pack_id: a.pack_id ?? pack.id ?? null,
        type: a.type,
        platform: a.platform,
        description: a.description || '',
        number_of_action: a.number_of_action ?? 1,
        disabled: false,
        total_currency: a.total_currency || '€',
        influencer_currency: a.influencer_currency || '€',
        total_amount: a.total_amount,
        influencer_amount: a.influencer_amount,
        variable_type: a.variable_type,
      })),
    }));
  }
  // Legacy flat actions — wrap into a single default pack.
  const legacyActions: any[] = activeInfluencer?.actions || [];
  return [
    {
      id: undefined,
      name: nextPackName([]),
      position: 0,
      total_amount: packSubtotal(legacyActions),
      influencer_amount: null,
      actions: legacyActions.map((a: any) => ({
        id: a.id,
        pack_id: a.pack_id ?? null,
        type: a.type,
        platform: a.platform,
        description: a.description || '',
        number_of_action: a.number_of_action ?? 1,
        disabled: false,
        total_currency: a.total_currency || '€',
        influencer_currency: a.influencer_currency || '€',
        total_amount: a.total_amount,
        influencer_amount: a.influencer_amount,
        variable_type: a.variable_type,
      })),
    },
  ];
}

function hydrateRecurrentPacks(activeInfluencer: any): TRecurrentPackForm[] {
  const existing: any[] = activeInfluencer?.packs || [];
  if (existing.length > 0) {
    return existing.map((pack: any, idx: number) => ({
      id: pack.id,
      name: pack.name,
      position: typeof pack.position === 'number' ? pack.position : idx,
      actions: (pack.actions || []).map((a: any) => ({
        id: a.id,
        pack_id: a.pack_id ?? pack.id ?? null,
        type: a.type,
        platform: a.platform,
        description: a.description || '',
        number_of_action: a.number_of_action ?? 1,
        month: a.month ?? 1,
        total_currency: a.total_currency || '€',
        influencer_currency: a.influencer_currency || '€',
        total_amount: a.total_amount,
        influencer_amount: a.influencer_amount,
        variable_type: a.variable_type,
      })),
    }));
  }
  const legacyActions: any[] = activeInfluencer?.actions || [];
  return [
    {
      id: undefined,
      name: nextPackName([]),
      position: 0,
      actions: legacyActions.map((a: any) => ({
        id: a.id,
        pack_id: a.pack_id ?? null,
        type: a.type,
        platform: a.platform,
        description: a.description || '',
        number_of_action: a.number_of_action ?? 1,
        month: a.month ?? 1,
        total_currency: a.total_currency || '€',
        influencer_currency: a.influencer_currency || '€',
        total_amount: a.total_amount,
        influencer_amount: a.influencer_amount,
        variable_type: a.variable_type,
      })),
    },
  ];
}

// ─── One-time form type ──────────────────────────────────────────────────────

type TOneTimePackForm = {
  rowId?: string;
  id?: string; // server-side id, omitted for new packs
  name: string;
  position: number;
  /**
   * Single price field for the pack. Defaults to the persisted value (or the
   * sum of the pack's actions when nothing is stored) and gets recalculated
   * to that sum whenever an action price changes after mount, but the user
   * can type over it freely at any time — it's a normal form field, only
   * persisted when the surrounding form is submitted (no separate
   * mutation/autosave/override flag).
   */
  total_amount?: number | string | null;
  /**
   * Influencer amount for this pack. Purely manual (never recalculated from
   * the actions); mirrored to ProposalUser.influencer_amount by the backend
   * when this pack is the selected one.
   */
  influencer_amount?: number | string | null;
  actions: TInfSettingsAction[];
};

type TProposalInfluencerSettingsForm = {
  total_amount: number;
  total_amountType: string;
  total_variable_amount: number;
  influencer_amount: number;
  influencer_amountType: string;
  influencer_variable_type: string;
  influencer_variable_amount: number;
  packs: TOneTimePackForm[];
};

// ─── Recurring form type ─────────────────────────────────────────────────────

type TRecurrentPackForm = {
  rowId?: string;
  id?: string;
  name: string;
  position: number;
  actions: TInfSettingsRecurrentAction[];
};

type TProposalInfluencerRecurrentForm = {
  total_amount: number;
  total_amountType: string;
  total_variable_amount: number;
  influencer_amount: number;
  influencer_amountType: string;
  influencer_variable_type: string;
  influencer_variable_amount: number;
  initDate: string | null;
  finishDate: string | null;
  cicle: boolean;
  packs: TRecurrentPackForm[];
  referrals: {
    id: string;
    type: string;
    referral: string;
    platforms: string;
    description: string;
  }[];
};

// ─── One-time Pack section (owns its nested actions useFieldArray) ──────────

type TOneTimePackSectionProps = {
  packId: string | undefined;
  packIndex: number;
  control: any;
  form: UseFormReturn<TProposalInfluencerSettingsForm>;
  canDelete: boolean;
  onRequestDelete: (packIndex: number) => void;
  onRequestDuplicate: (packIndex: number) => void;
  campaignCurrency: string;
};

function OneTimePackSection({
  packId: _packId,
  packIndex,
  control,
  form,
  canDelete,
  onRequestDelete,
  onRequestDuplicate,
  campaignCurrency,
}: TOneTimePackSectionProps) {
  const { t } = useTranslation('common');
  const {
    fields: rawActions,
    append: addAction,
    remove: removeAction,
    update: updateAction,
  } = useFieldArray({
    control,
    name: `packs.${packIndex}.actions`,
    keyName: 'rowId',
  });

  const actions = rawActions as unknown as TInfSettingsAction[];

  // useWatch (not form.watch) guarantees this component re-renders whenever
  // any nested action field changes — including total_amount, which is set
  // directly by MoneyInput's own Controller, bypassing useFieldArray's
  // `update`.
  const packActions =
    useWatch({ control, name: `packs.${packIndex}.actions` }) || [];
  const subtotal = useMemo(() => packSubtotal(packActions), [packActions]);
  // The pack influencer amount shares the currency picked in the top-level
  // "Importe Influencer" selector (ProposalUser.influencer_amount_type),
  // which is the same currency carried over to CampaignUser.
  const influencerCurrency =
    useWatch({ control, name: 'influencer_amountType' }) || '€';

  // The pack total is a single, always-editable form field
  // (`packs.${packIndex}.total_amount`). Whenever an action price changes
  // after mount, recalculate it to the fresh sum — overwriting whatever was
  // typed in it before. The initial run (mount/remount) is skipped on
  // purpose so the persisted value hydrated from the DB is never clobbered
  // by the actions subtotal. The user can still type over it freely at any
  // other time; that typed value just sits in the form until either the next
  // action edit recalculates it again, or the surrounding form is submitted
  // ("Actualizar datos"), which persists whatever is currently in the field.
  // No separate mutation, no override flag — just one field.
  const prevSubtotalRef = useRef(subtotal);
  useEffect(() => {
    if (prevSubtotalRef.current === subtotal) return;
    prevSubtotalRef.current = subtotal;
    form.setValue(`packs.${packIndex}.total_amount`, subtotal);
  }, [subtotal]);

  return (
    <div className="rounded-10 p-4 flex flex-col gap-4 bg-dark-blue text-white">
      <div className="flex flex-row items-center gap-3 justify-between flex-wrap">
        <Controller
          control={control}
          name={`packs.${packIndex}.name`}
          render={({ field }) => (
            <TextInput
              type="text"
              placeholder={t('singleCampaign.packNamePlaceholder')}
              selectedValue={field.value}
              extraClassname="bg-white font-semibold text-dark-blue max-w-[220px]"
              onBlur={(e: any) => field.onChange(e.target.value)}
            />
          )}
        />
        <Controller
          control={control}
          name={`packs.${packIndex}.total_amount`}
          render={({ field }) => (
            <PackAmountPill
              label={t('singleCampaign.precio')}
              value={field.value}
              onChange={field.onChange}
              currency={campaignCurrency}
            />
          )}
        />
        <Controller
          control={control}
          name={`packs.${packIndex}.influencer_amount`}
          render={({ field }) => (
            <PackAmountPill
              label={t('singleCampaign.influencerPrice')}
              value={field.value}
              onChange={field.onChange}
              currency={influencerCurrency}
            />
          )}
        />
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            className="text-[10px] text-influentia-light-blue font-medium border-b border-influentia-light-blue hover:text-white hover:border-white"
            onClick={() => onRequestDuplicate(packIndex)}
          >
            {t('singleCampaign.duplicate')}
          </button>
          {canDelete ? (
            <button
              type="button"
              className="text-red-status hover:text-influentia-red"
              onClick={() => onRequestDelete(packIndex)}
              aria-label={t('global.delete')}
            >
              <TrashIcon className="size-[15px]" />
            </button>
          ) : (
            <span
              className="text-gray-hover"
              title={t('singleCampaign.lastPackCannotDelete')}
              aria-disabled
            >
              <TrashIcon className="size-[15px]" />
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {actions.map((action, i) => (
          <ProposalActionSettings
            key={action.id}
            control={control}
            action={action}
            updateAction={(key, val) => {
              updateAction(i, {
                ...form.watch(`packs.${packIndex}.actions.${i}`),
                [key]: val,
              } as any);
            }}
            deleteAction={() => removeAction(i)}
            index={i}
            fieldPathPrefix={`packs.${packIndex}.actions`}
            campaignCurrency={campaignCurrency}
          />
        ))}
      </div>
      <div className="w-full flex items-center justify-center">
        <button
          type="button"
          onClick={() =>
            addAction({
              id: newRowId(),
              type: 'Dedicated_Video',
              platform: 'Youtube',
              description: '',
              number_of_action: 1,
              disabled: false,
              total_currency: '€',
              influencer_currency: '€',
            })
          }
        >
          <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
            <CheckBoxReferralIcon />
            <span className="text-[10px] text-influentia-light-blue font-medium">
              {t('singleCampaign.addAction')}
            </span>
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── One-time variant ────────────────────────────────────────────────────────

function OneTimeInfluencerSettings({
  activeInfluencer,
  refetch,
  campaignCurrency,
  proposalId,
}: {
  activeInfluencer: any;
  refetch: () => void;
  campaignCurrency: string | null;
  proposalId: string;
}) {
  const { t } = useTranslation('common');
  const { notifySaveSuccess, notifySaveError, notifyWarning, notifyError } =
    useNotification();
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);

  const form = useForm<TProposalInfluencerSettingsForm>({
    defaultValues: {
      packs: [],
    },
  });
  const { control, reset, watch } = form;

  const {
    fields: packs,
    append: appendPack,
    remove: removePack,
    update: updatePack,
  } = useFieldArray({
    control,
    name: 'packs',
    keyName: 'rowId',
  });

  useEffect(() => {
    if (!activeInfluencer) return;
    reset({
      total_amount: activeInfluencer.total_amount ?? 0,
      total_amountType: campaignCurrency ?? '€',
      total_variable_amount: activeInfluencer.total_variable_amount ?? 0,
      influencer_amount: activeInfluencer.influencer_amount ?? 0,
      influencer_amountType: activeInfluencer.influencer_amount_type ?? '€',
      influencer_variable_type:
        activeInfluencer.influencer_variable_type ?? 'No',
      influencer_variable_amount:
        activeInfluencer.influencer_variable_amount ?? 0,
      packs: hydrateOneTimePacks(activeInfluencer),
    });
  }, [activeInfluencer?.id]);

  const handleAddPack = () => {
    const existingNames = watch('packs').map((p) => p.name);
    appendPack({
      id: undefined,
      name: nextPackName(existingNames),
      position: watch('packs').length,
      total_amount: 0,
      influencer_amount: null,
      actions: [],
    });
  };

  const handleDuplicatePack = (packIndex: number) => {
    const source = watch(`packs.${packIndex}`);
    if (!source) return;
    const existingNames = watch('packs').map((p) => p.name);
    appendPack({
      id: undefined,
      name: nextPackName(existingNames),
      position: watch('packs').length,
      // Copy the source pack's prices as-is (including a manual total); the
      // pack total is only recalculated later if an action price changes.
      total_amount: source.total_amount ?? packSubtotal(source.actions || []),
      influencer_amount: source.influencer_amount ?? null,
      actions: source.actions.map((a: TInfSettingsAction) => ({
        ...a,
        id: newRowId(),
        pack_id: null,
      })),
    });
  };

  const handleRequestDeletePack = (packIndex: number) => {
    if (packs.length <= 1) {
      notifyWarning(t('singleCampaign.lastPackCannotDelete'));
      return;
    }
    const pack = watch(`packs.${packIndex}`);
    const hasActions = (pack?.actions || []).length > 0;
    if (hasActions) {
      setPendingDelete(packIndex);
    } else {
      removePack(packIndex);
    }
  };

  const handleConfirmDeletePack = () => {
    if (pendingDelete == null) return;
    removePack(pendingDelete);
    setPendingDelete(null);
  };

  const [updateProposalUser, { loading: saving }] =
    useMutation(UPDATE_PROPOSAL_USER);

  const handleSave = () => {
    if (!activeInfluencer) return;
    if (packs.length === 0) {
      notifyWarning(t('singleCampaign.lastPackCannotDelete'));
      return;
    }

    // Defense-in-depth: the Precio field onBlur normalizes negatives to null,
    // but block submission if any action still carries a negative value
    // (covers manual entry or form tampering) — server has its own guard.
    const watchedPacks = watch('packs');
    const hasNegativePrecio = watchedPacks.some((pack) =>
      (pack.actions || []).some((action) => {
        const ta = Number(action.total_amount);
        const ia = Number(action.influencer_amount);
        return (!Number.isNaN(ta) && ta < 0) || (!Number.isNaN(ia) && ia < 0);
      }),
    );
    if (hasNegativePrecio) {
      notifyError(t('singleCampaign.precioCannotBeNegative'));
      return;
    }

    // Re-position packs sequentially in case of deletes/reorders.
    const packsPayload = watch('packs').map((pack, idx) => ({
      ...(pack.id ? { id: pack.id } : {}),
      name: pack.name,
      position: idx,
      total_amount:
        pack.total_amount === undefined || pack.total_amount === null
          ? null
          : Number(pack.total_amount),
      influencer_amount:
        pack.influencer_amount === undefined ||
        pack.influencer_amount === null ||
        pack.influencer_amount === ''
          ? null
          : Number(pack.influencer_amount),
      actions: (pack.actions || []).map((action) => ({
        ...(action.id ? { id: action.id } : {}),
        type: action.type,
        platform: action.platform,
        description: action.description || '',
        number_of_action: action.number_of_action ?? 1,
        variable_type: action.variable_type || null,
        total_amount:
          action.total_amount === undefined || action.total_amount === null
            ? null
            : Number(action.total_amount),
        total_currency: campaignCurrency ?? '€',
        influencer_amount:
          action.influencer_amount === undefined ||
          action.influencer_amount === null
            ? null
            : Number(action.influencer_amount),
        influencer_currency: action.influencer_currency || '€',
      })),
    }));

    updateProposalUser({
      variables: {
        id: activeInfluencer.id,
        total_amount: Number(watch('total_amount')),
        influencer_amount: Number(watch('influencer_amount')),
        influencer_amount_type: watch('influencer_amountType'),
        total_variable_amount: Number(watch('total_variable_amount')),
        influencer_variable_type: watch('influencer_variable_type') || 'No',
        influencer_variable_amount: Number(watch('influencer_variable_amount')),
        packs: packsPayload,
      },
    })
      .then((res) => {
        if (res.data?.updateProposalUser?.error) {
          notifySaveError();
          return;
        }
        notifySaveSuccess();
        refetch();
      })
      .catch(() => notifySaveError());
  };

  const watchedActionsForPricing = useMemo(() => {
    // Flatten pack actions for CampaignPriceSections which still expects a
    // flat array shape (it only reads variable_type to drive its UI).
    return watch('packs').flatMap((p) => p.actions || []);
  }, [watch('packs')]);

  return (
    <div className="flex-1 pb-20">
      <Confirm
        open={pendingDelete != null}
        onConfirm={handleConfirmDeletePack}
        setOpen={(open) => !open && setPendingDelete(null)}
        type="delete"
        title={t('singleCampaign.deletePackWithActionsTitle')}
        subtitle={t('singleCampaign.deletePackWithActionsSubtitle')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
      />
      <CampaignPriceSections
        actions={watchedActionsForPricing}
        control={control}
        influencer_variable_type={watch('influencer_variable_type')}
        watch={watch}
        totalAmountDisabled
      />
      <div className="mt-5 flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <span>{t('global.actions')}</span>
          <button type="button" id="addPack" onClick={handleAddPack}>
            <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
              <CheckBoxReferralIcon />
              <span className="text-[10px] text-influentia-light-blue font-medium">
                {t('singleCampaign.addPack')}
              </span>
            </div>
          </button>
        </div>
        {packs.map((pack, i) => (
          <OneTimePackSection
            key={pack.id ?? pack.rowId ?? `pack-${i}`}
            packId={pack.id}
            packIndex={i}
            control={control}
            form={form}
            canDelete={packs.length > 1}
            onRequestDelete={handleRequestDeletePack}
            onRequestDuplicate={handleDuplicatePack}
            campaignCurrency={campaignCurrency ?? '€'}
          />
        ))}
        {packs.length === 0 && (
          <p className="text-gray-400 text-sm italic">
            {t('singleCampaign.noPacks')}
          </p>
        )}
      </div>
      <div className="mt-5 lg:items-end flex lg:justify-end justify-center w-full">
        <Button
          id="saveInfluencerSettings"
          style="light-blue"
          onClick={handleSave}
          loading={saving}
          text={t('singleCampaign.updateInfo')}
        />
      </div>
    </div>
  );
}

// ─── Recurring Pack section (owns its nested actions useFieldArray) ──────────

type TRecurrentPackSectionProps = {
  packIndex: number;
  control: any;
  form: UseFormReturn<TProposalInfluencerRecurrentForm>;
  months: number[];
  onAddMonth: () => void;
  onRemoveMonth: (month: number) => void;
  canDelete: boolean;
  onRequestDelete: (packIndex: number) => void;
  onRequestDuplicate: (packIndex: number) => void;
  campaignCurrency: string;
};

function RecurrentPackSection({
  packIndex,
  control,
  form,
  months,
  onAddMonth,
  onRemoveMonth,
  canDelete,
  onRequestDelete,
  onRequestDuplicate,
  campaignCurrency,
}: TRecurrentPackSectionProps) {
  const { t } = useTranslation('common');
  const {
    fields: rawActions,
    append: addAction,
    remove: removeAction,
    update: updateAction,
  } = useFieldArray({
    control,
    name: `packs.${packIndex}.actions`,
    keyName: 'rowId',
  });

  const actions = rawActions as unknown as TInfSettingsRecurrentAction[];

  // useWatch (not form.watch) guarantees this component re-renders whenever
  // any nested action field changes — see OneTimePackSection for details.
  const packActions: TInfSettingsRecurrentAction[] =
    useWatch({ control, name: `packs.${packIndex}.actions` }) || [];

  // Per-pack month totals — months are scoped to actions belonging to this pack.
  const monthTotalsByPack = useMemo(() => {
    const totals: Record<number, number> = {};
    packActions.forEach((a) => {
      const m = a.month ?? 1;
      const v = a.total_amount;
      // MoneyInput stores its value as a raw formatted string in RHF state,
      // not a number — coerce before summing.
      const parsed = typeof v === 'string' ? Number(v) : v;
      totals[m] =
        (totals[m] || 0) +
        (typeof parsed === 'number' && !Number.isNaN(parsed) ? parsed : 0);
    });
    return totals;
  }, [packActions]);

  return (
    <div className="rounded-10 p-4 flex flex-col gap-4 bg-dark-blue text-white">
      <div className="flex flex-row items-center gap-3 justify-between flex-wrap">
        <Controller
          control={control}
          name={`packs.${packIndex}.name`}
          render={({ field }) => (
            <TextInput
              type="text"
              placeholder={t('singleCampaign.packNamePlaceholder')}
              selectedValue={field.value}
              extraClassname="bg-white font-semibold text-dark-blue max-w-[220px]"
              onBlur={(e: any) => field.onChange(e.target.value)}
            />
          )}
        />
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            className="text-[10px] text-influentia-light-blue font-medium border-b border-influentia-light-blue hover:text-white hover:border-white"
            onClick={() => onRequestDuplicate(packIndex)}
          >
            {t('singleCampaign.duplicate')}
          </button>
          {canDelete ? (
            <button
              type="button"
              className="text-red-status hover:text-influentia-red"
              onClick={() => onRequestDelete(packIndex)}
              aria-label={t('global.delete')}
            >
              <TrashIcon className="size-[15px]" />
            </button>
          ) : (
            <span
              className="text-gray-hover"
              title={t('singleCampaign.lastPackCannotDelete')}
              aria-disabled
            >
              <TrashIcon className="size-[15px]" />
            </span>
          )}
        </div>
      </div>

      {/* Months nested inside this pack */}
      <div className="flex flex-col gap-3">
        {months.map((month, indexMonth) => (
          <div className="mt-2" key={month}>
            <MonthActionRecurrent month={month} removeMonth={onRemoveMonth} />
            <div className="flex flex-row justify-between items-center mt-2">
              <span className="text-xs font-semibold whitespace-nowrap bg-influentia-light-blue text-dark-blue px-3 py-1 rounded-full">
                {t('singleCampaignRecurrent.monthTotal')} ·{' '}
                {formatCurrency(
                  monthTotalsByPack[month] || 0,
                  campaignCurrency,
                )}
              </span>
            </div>
            <div className="flex flex-col gap-3 mt-3">
              {actions.map((action, i) =>
                action.month === month ? (
                  <ProposalActionSettingsRecurrent
                    key={action.id}
                    control={control}
                    action={action}
                    updateAction={(key, val) =>
                      updateAction(i, { ...action, [key]: val } as any)
                    }
                    deleteAction={() => removeAction(i)}
                    index={i}
                    fieldPathPrefix={`packs.${packIndex}.actions`}
                    campaignCurrency={campaignCurrency}
                  />
                ) : null,
              )}
            </div>
            <div className="w-full flex items-center justify-center">
              <button
                type="button"
                onClick={() =>
                  addAction({
                    id: newRowId(),
                    type: 'Dedicated_Video',
                    platform: 'Youtube',
                    description: '',
                    number_of_action: 1,
                    month,
                  })
                }
              >
                <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
                  <CheckBoxReferralIcon />
                  <span className="text-[10px] text-influentia-light-blue font-medium">
                    {t('singleCampaign.addAction')}
                  </span>
                </div>
              </button>
              {indexMonth === months.length - 1 && (
                <button className="ml-5" type="button" onClick={onAddMonth}>
                  <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
                    <CalendarIcon className="text-influentia-light-blue size-[14px]" />
                    <span className="text-[10px] text-influentia-light-blue font-medium">
                      {t('singleCampaignRecurrent.addMonth')}
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Recurring variant ──────────────────────────────────────────────────────

function RecurringInfluencerSettings({
  activeInfluencer,
  refetch,
  campaignCurrency,
}: {
  activeInfluencer: any;
  refetch: () => void;
  campaignCurrency: string | null;
}) {
  const { t } = useTranslation('common');
  const { notifyError, notifySaveSuccess, notifySaveError, notifyWarning } =
    useNotification();
  const [months, setMonths] = useState<number[]>([]);
  const [confirmPopupCicle, setConfirmPopupCicle] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);

  const form = useForm<TProposalInfluencerRecurrentForm>({
    defaultValues: {
      packs: [],
      referrals: [],
    },
  });
  const { control, reset, watch, setValue } = form;
  const { cicle } = watch();

  const {
    fields: packs,
    append: appendPack,
    remove: removePack,
  } = useFieldArray({
    control,
    name: 'packs',
    keyName: 'rowId',
  });

  const {
    fields: referrals,
    append: addReferral,
    remove: removeReferral,
    update: updateReferral,
  } = useFieldArray({ control, name: 'referrals', keyName: 'rowId' });

  useEffect(() => {
    if (!activeInfluencer) return;
    const packsData = hydrateRecurrentPacks(activeInfluencer);
    reset({
      total_amount: activeInfluencer.total_amount ?? 0,
      total_amountType: campaignCurrency ?? '€',
      total_variable_amount: activeInfluencer.total_variable_amount ?? 0,
      influencer_amount: activeInfluencer.influencer_amount ?? 0,
      influencer_amountType: activeInfluencer.influencer_amount_type ?? '€',
      influencer_variable_type:
        activeInfluencer.influencer_variable_type ?? 'No',
      influencer_variable_amount:
        activeInfluencer.influencer_variable_amount ?? 0,
      initDate: activeInfluencer.initDate ?? null,
      finishDate: activeInfluencer.finishDate ?? null,
      cicle: activeInfluencer.cicle ?? false,
      packs: packsData,
      referrals: [],
    });

    // months are derived from cicle + actions across all packs
    const maxMonth =
      packsData.length > 0 && packsData.some((p) => p.actions.length > 0)
        ? Math.max(
            ...packsData.flatMap((p) => p.actions.map((a) => a.month ?? 1)),
            1,
          )
        : 1;
    setMonths(Array.from({ length: maxMonth }, (_, i) => i + 1));
  }, [activeInfluencer?.id]);

  const handleAddPack = () => {
    const existingNames = watch('packs').map((p) => p.name);
    appendPack({
      id: undefined,
      name: nextPackName(existingNames),
      position: watch('packs').length,
      actions: [],
    });
  };

  const handleDuplicatePack = (packIndex: number) => {
    const source = watch(`packs.${packIndex}`);
    if (!source) return;
    const existingNames = watch('packs').map((p) => p.name);
    appendPack({
      id: undefined,
      name: nextPackName(existingNames),
      position: watch('packs').length,
      actions: source.actions.map((a: TInfSettingsRecurrentAction) => ({
        ...a,
        id: newRowId(),
        pack_id: null,
      })),
    });
  };

  const handleRequestDeletePack = (packIndex: number) => {
    if (packs.length <= 1) {
      notifyWarning(t('singleCampaign.lastPackCannotDelete'));
      return;
    }
    const pack = watch(`packs.${packIndex}`);
    const hasActions = (pack?.actions || []).length > 0;
    if (hasActions) {
      setPendingDelete(packIndex);
    } else {
      removePack(packIndex);
    }
  };

  const handleConfirmDeletePack = () => {
    if (pendingDelete == null) return;
    removePack(pendingDelete);
    setPendingDelete(null);
  };

  const handleNewMonth = () => {
    setMonths((prev) => [...prev, Math.max(...prev) + 1]);
  };

  const removeMonth = (month: number) => {
    setMonths((prev) => {
      const next = prev.filter((m) => m !== month);
      return next.map((m) => (m > month ? m - 1 : m));
    });
    // remove from all packs at once — month is pack-scoped for display only
    const packsState = watch('packs');
    packsState.forEach((pack, packIdx) => {
      const remaining = pack.actions
        .filter((a) => a.month === month)
        .map((_, i) => pack.actions.indexOf(pack.actions[i]))
        .reverse();
      remaining.forEach((idx) => {
        // Best-effort nested remove; for simplicity rely on save-time diff.
      });
      pack.actions
        .filter((a) => a.month === month)
        .forEach(() => {
          // actual removal happens on save via diff-upsert
        });
      // Shift months higher than removed month down by 1.
      setValue(
        `packs.${packIdx}.actions`,
        pack.actions
          .filter((a) => a.month !== month)
          .map((a) => (a.month > month ? { ...a, month: a.month - 1 } : a)),
        { shouldDirty: true },
      );
    });
  };

  const handleNewReferral = (type: string) => {
    addReferral({
      id: newRowId(),
      type,
      referral: '',
      platforms: '',
      description: '',
    });
  };

  const onChangeCicle = (val: boolean) => {
    if (val) setValue('cicle', true);
    else {
      setConfirmPopupCicle(true);
      setValue('cicle', false);
    }
  };

  const handleConfirmCicle = () => {
    setMonths([1]);
    const packsState = watch('packs');
    packsState.forEach((pack, packIdx) => {
      setValue(
        `packs.${packIdx}.actions`,
        pack.actions.filter((a) => (a.month ?? 1) === 1),
        { shouldDirty: true },
      );
    });
  };

  const [updateProposalUser, { loading: saving }] =
    useMutation(UPDATE_PROPOSAL_USER);

  const handleSave = () => {
    if (Number(watch('influencer_amount')) === 0) {
      notifyError(t('singleCampaign.influencerPriceRequired'));
      return;
    }
    if (packs.length === 0) {
      notifyError(t('singleCampaign.lastPackCannotDelete'));
      return;
    }
    if (!watch('initDate')) {
      notifyError(t('singleCampaignRecurrent.initDateRequired'));
      return;
    }

    // Defense-in-depth: the Precio field onBlur normalizes negatives to null,
    // but block submission if any action still carries a negative value
    // (covers manual entry or form tampering) — server has its own guard.
    const watchedPacks = watch('packs');
    const hasNegativePrecio = watchedPacks.some((pack) =>
      (pack.actions || []).some((action) => {
        const ta = Number(action.total_amount);
        const ia = Number(action.influencer_amount);
        return (!Number.isNaN(ta) && ta < 0) || (!Number.isNaN(ia) && ia < 0);
      }),
    );
    if (hasNegativePrecio) {
      notifyError(t('singleCampaign.precioCannotBeNegative'));
      return;
    }

    const packsPayload = watch('packs').map((pack, idx) => ({
      ...(pack.id ? { id: pack.id } : {}),
      name: pack.name,
      position: idx,
      actions: (pack.actions || []).map((action) => ({
        ...(action.id ? { id: action.id } : {}),
        type: action.type,
        platform: action.platform,
        description: action.description || '',
        number_of_action: action.number_of_action ?? 1,
        month: action.month ?? 1,
        variable_type: action.variable_type || null,
        total_amount:
          action.total_amount === undefined || action.total_amount === null
            ? null
            : Number(action.total_amount),
        total_currency: campaignCurrency ?? '€',
        influencer_amount:
          action.influencer_amount === undefined ||
          action.influencer_amount === null
            ? null
            : Number(action.influencer_amount),
        influencer_currency: action.influencer_currency || '€',
      })),
    }));

    updateProposalUser({
      variables: {
        id: activeInfluencer.id,
        influencer_amount: Number(watch('influencer_amount')),
        influencer_amount_type: watch('influencer_amountType'),
        total_amount: Number(watch('total_amount')),
        total_variable_amount: Number(watch('total_variable_amount')),
        influencer_variable_type: watch('influencer_variable_type') || 'No',
        influencer_variable_amount: Number(watch('influencer_variable_amount')),
        initDate: watch('initDate'),
        finishDate: watch('finishDate'),
        cicle: watch('cicle'),
        packs: packsPayload,
      },
    })
      .then((res) => {
        if (res.data?.updateProposalUser?.error) {
          notifySaveError();
          return;
        }
        notifySaveSuccess();
        refetch();
      })
      .catch(() => notifySaveError());
  };

  const watchedActionsForPricing = useMemo(() => {
    return watch('packs').flatMap((p) => p.actions || []);
  }, [watch('packs')]);

  return (
    <>
      <Confirm
        open={confirmPopupCicle}
        onConfirm={handleConfirmCicle}
        setOpen={setConfirmPopupCicle}
        type="info"
        title={t('singleCampaign.changeCicleTitle')}
        subtitle={t('singleCampaign.changeCicleSubTitle')}
        confirmText={t('singleCampaign.changeCicleConfirm')}
        cancelText={t('global.cancel')}
      />
      <Confirm
        open={pendingDelete != null}
        onConfirm={handleConfirmDeletePack}
        setOpen={(open) => !open && setPendingDelete(null)}
        type="delete"
        title={t('singleCampaign.deletePackWithActionsTitle')}
        subtitle={t('singleCampaign.deletePackWithActionsSubtitle')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
      />
      <form autoComplete="off" className="pb-20">
        {/* Dates */}
        <div className="flex lg:flex-row flex-col w-full gap-5 mb-5">
          <div className="flex flex-row w-full">
            <DatePickerInput
              name="initDate"
              monthYearPicker
              isClearable
              validation={{
                required: t('singleCampaignRecurrent.initDateRequired'),
              }}
              label={t('singleCampaignRecurrent.initDate')}
              control={control}
            />
          </div>
          <div className="flex flex-row w-full">
            <DatePickerInput
              monthYearPicker
              isClearable
              name="finishDate"
              tooltipText={t('singleCampaignRecurrent.finishDateTooltip')}
              label={t('singleCampaignRecurrent.finishDate')}
              control={control}
            />
          </div>
        </div>

        {/* Prices */}
        <CampaignPriceSections
          actions={watchedActionsForPricing}
          control={control}
          influencer_variable_type={watch('influencer_variable_type')}
          watch={watch}
          totalAmountDisabled
        />

        {/* Referrals */}
        <div className="flex lg:flex-row flex-col mt-5 gap-8">
          <div className="w-full flex flex-col">
            <span>{t('singleCampaign.referrerCode')}</span>
            <div className="w-full flex flex-col gap-1">
              {referrals.map((referral, i) =>
                referral.type === 'Code' ? (
                  <ReferralCode
                    key={referral.id}
                    referral={referral}
                    placeholderDescription={t(
                      'singleCampaign.referralCodeDescription',
                    )}
                    placeholderReferral={t('singleCampaign.referralCode')}
                    updateReferral={(key, val) =>
                      updateReferral(i, { ...referral, [key]: val } as any)
                    }
                    deleteReferral={() => removeReferral(i)}
                  />
                ) : null,
              )}
            </div>
            <button
              type="button"
              className="w-full flex items-center justify-center"
              onClick={() => handleNewReferral('Code')}
            >
              <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
                <CheckBoxReferralIcon />
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.addCode')}
                </span>
              </div>
            </button>
          </div>
          <div className="w-full">
            <span>{t('singleCampaign.referrerLink')}</span>
            <div className="w-full flex flex-col gap-1">
              {referrals.map((referral, i) =>
                referral.type === 'Link' ? (
                  <ReferralCode
                    key={referral.id}
                    referral={referral}
                    placeholderDescription={t(
                      'singleCampaign.referralLinkDescription',
                    )}
                    placeholderReferral={t('singleCampaign.referralLink')}
                    updateReferral={(key, val) =>
                      updateReferral(i, { ...referral, [key]: val } as any)
                    }
                    deleteReferral={() => removeReferral(i)}
                  />
                ) : null,
              )}
            </div>
            <div className="w-full flex items-center justify-center">
              <button type="button" onClick={() => handleNewReferral('Link')}>
                <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
                  <CheckBoxReferralIcon />
                  <span className="text-[10px] text-influentia-light-blue font-medium">
                    {t('singleCampaign.addLink')}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Packs (outer grouping) */}
        <div className="mt-5">
          <div className="flex flex-row justify-between items-center">
            <p>{t('global.actions')}</p>
            <div className="flex flex-row items-center gap-3">
              <Toggle
                label={t('singleCampaignRecurrent.cicle')}
                value={cicle}
                onChange={() => onChangeCicle(!cicle)}
                maxWidth="max-w-[200px]"
              />
              <button type="button" id="addPack" onClick={handleAddPack}>
                <div className="flex flex-row items-center justify-center mt-1 gap-1 border-b border-influentia-light-blue w-fit">
                  <CheckBoxReferralIcon />
                  <span className="text-[10px] text-influentia-light-blue font-medium">
                    {t('singleCampaign.addPack')}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3 mt-3">
            {packs.map((pack, i) => (
              <RecurrentPackSection
                key={pack.id ?? pack.rowId ?? `pack-${i}`}
                packIndex={i}
                control={control}
                form={form}
                months={cicle ? months : [1]}
                onAddMonth={handleNewMonth}
                onRemoveMonth={removeMonth}
                canDelete={packs.length > 1}
                onRequestDelete={handleRequestDeletePack}
                onRequestDuplicate={handleDuplicatePack}
                campaignCurrency={campaignCurrency ?? '€'}
              />
            ))}
            {packs.length === 0 && (
              <p className="text-gray-400 text-sm italic">
                {t('singleCampaign.noPacks')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 lg:items-end flex lg:justify-end justify-center w-full">
          <Button
            id="saveInfluencerSettings"
            style="light-blue"
            onClick={handleSave}
            loading={saving}
            text={t('singleCampaign.updateInfo')}
          />
        </div>
      </form>
    </>
  );
}

// ─── Rejection Banner ────────────────────────────────────────────────────────

function RejectionBanner({
  activeInfluencer,
  refetch,
}: {
  activeInfluencer: any;
  refetch: () => void;
}) {
  const { t } = useTranslation('common');
  const { notifySaveSuccess, notifySaveError } = useNotification();
  const { proposal } = useOutletContext<SingleProposalOutletContext>();
  const isRejected = activeInfluencer?.status === 'rejected';
  const proposalApproved = proposal.status === 'approved';

  const [updateStatus, { loading: updatingStatus }] = useMutation(
    UPDATE_PROPOSAL_USER_STATUS,
  );

  const handleToggleStatus = () => {
    const newStatus: ProposalInfluencerStatus = isRejected
      ? 'active'
      : 'rejected';
    updateStatus({
      variables: {
        id: activeInfluencer.id,
        status: newStatus,
      },
    })
      .then((res) => {
        if (res.data?.updateProposalUserStatus?.error) {
          notifySaveError();
          return;
        }
        notifySaveSuccess();
        refetch();
      })
      .catch(() => notifySaveError());
  };

  if (proposalApproved) return null;

  return (
    <div
      className={`mb-4 p-3 rounded-lg border ${
        isRejected
          ? 'bg-influentia-yellow border-red-200'
          : 'bg-gray-50 border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isRejected && <WarningIcon className="text-dark-blue size-5" />}
          <div>
            {isRejected ? (
              <>
                <p className="text-sm font-semibold text-dark-blue">
                  {t('proposals.influencerRejected')}
                </p>
                <p className="text-xs text-dark-blue">
                  {t('proposals.influencerRejectedDesc')}
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-gray-700">
                {t('proposals.influencerActive')}
              </p>
            )}
          </div>
        </div>
        <Button
          id={isRejected ? 'reincludedInfluencer' : 'rejectInfluencer'}
          style="dark-blue"
          customClassnames={
            isRejected ? '' : '!bg-red-status !text-white hover:!bg-red-700'
          }
          onClick={handleToggleStatus}
          loading={updatingStatus}
          text={
            isRejected
              ? t('proposals.reincludedInfluencer')
              : t('proposals.rejectInfluencer')
          }
        />
      </div>
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────────────────────

export function SingleProposalInfluencerSettingsTab() {
  const { t } = useTranslation('common');
  const { proposal, refetch } = useOutletContext<SingleProposalOutletContext>();
  const { influencerId: activeInfluencerId } = useParams<{
    influencerId: string;
  }>();

  const activeInfluencer =
    proposal.ProposalUsers.find(
      (u) => u.id === (activeInfluencerId ?? proposal.ProposalUsers[0]?.id),
    ) ?? proposal.ProposalUsers[0];

  if (proposal.ProposalUsers.length === 0) {
    return (
      <p className="text-gray-400 text-sm italic">{t('global.no-data')}</p>
    );
  }

  return (
    <div>
      <RejectionBanner activeInfluencer={activeInfluencer} refetch={refetch} />
      {proposal.campaign_type === 'recurring' ? (
        <RecurringInfluencerSettings
          campaignCurrency={proposal.currency}
          activeInfluencer={activeInfluencer}
          refetch={refetch}
        />
      ) : (
        <OneTimeInfluencerSettings
          campaignCurrency={proposal.currency}
          activeInfluencer={activeInfluencer}
          refetch={refetch}
          proposalId={proposal.id}
        />
      )}
    </div>
  );
}
